import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  MessageCircle,
  Send,
  User,
  Clock,
  Heart,
  ThumbsUp,
  ThumbsDown,
  Tag,
} from 'lucide-react';
import { getArabicDate } from '../../services/utils/date';

const QuestionSection = ({ 
  questions = [], 
  isLoading, 
  onSubmitQuestion, 
  onVoteQuestion, 
  onFavoriteQuestion,
  isSubmitting = false 
}) => {
  const [questionText, setQuestionText] = useState('');
  const [showQuestionForm, setShowQuestionForm] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!questionText.trim()) return;

    try {
      await onSubmitQuestion(questionText);
      setQuestionText('');
      setShowQuestionForm(false);
    } catch (error) {
      // Error submitting question
    }
  };

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-4">
        {[1, 2, 3].map(i => (
          <div key={i} className="bg-gray-100 h-32 rounded-2xl" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Question Form Toggle */}
      {!showQuestionForm ? (
        <button
          onClick={() => setShowQuestionForm(true)}
          className="w-full bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 text-white py-4 px-6 rounded-2xl font-bold transition-all transform hover:scale-[1.02] shadow-lg shadow-primary-200/50 flex items-center justify-center gap-3"
          aria-label="اطرح سؤالاً"
        >
          <MessageCircle size={20} />
          اطرح سؤالاً للمدرس
        </button>
      ) : (
        <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-6 border-2 border-primary-100 shadow-lg">
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-primary-100 p-3 rounded-xl">
              <MessageCircle className="text-primary-600" size={20} />
            </div>
            <h3 className="font-bold text-gray-900 text-lg">سؤال جديد</h3>
          </div>

          <textarea
            value={questionText}
            onChange={(e) => setQuestionText(e.target.value)}
            placeholder="اكتب سؤالك هنا... سيرد عليك المدرس في أقرب وقت"
            className="w-full border-2 border-gray-200 rounded-xl p-4 mb-4 min-h-[120px] focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-all resize-none"
            disabled={isSubmitting}
            aria-label="نص السؤال"
            required
          />

          <div className="flex gap-3">
            <button
              type="submit"
              disabled={isSubmitting || !questionText.trim()}
              className="flex-1 bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 text-white py-3 px-6 rounded-xl font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              aria-label="إرسال السؤال"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                  جاري الإرسال...
                </>
              ) : (
                <>
                  <Send size={18} />
                  إرسال السؤال
                </>
              )}
            </button>

            <button
              type="button"
              onClick={() => {
                setShowQuestionForm(false);
                setQuestionText('');
              }}
              className="px-6 py-3 border-2 border-gray-200 rounded-xl font-bold hover:bg-gray-50 transition-all"
              disabled={isSubmitting}
              aria-label="إلغاء"
            >
              إلغاء
            </button>
          </div>
        </form>
      )}

      {/* Questions List */}
      <div className="space-y-4">
        {questions && questions.length > 0 ? (
          questions.map((q) => (
            <QuestionCard
              key={q.id}
              question={q}
              onVote={onVoteQuestion}
              onFavorite={onFavoriteQuestion}
            />
          ))
        ) : (
          <div className="text-center py-12">
            <div className="bg-gray-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <MessageCircle className="text-gray-300" size={24} />
            </div>
            <h3 className="text-lg font-bold text-gray-500 mb-2">لا توجد أسئلة بعد</h3>
            <p className="text-gray-400">كن أول من يسأل!</p>
          </div>
        )}
      </div>
    </div>
  );
};

const QuestionCard = ({ question, onVote, onFavorite }) => {
  const handleVote = (voteType) => {
    onVote({ questionId: question.id, voteType });
  };

  const handleFavorite = () => {
    onFavorite(question.id);
  };

  return (
    <div className="bg-white rounded-2xl p-6 border border-gray-100 hover:border-primary-100 transition-all hover:shadow-lg">
      {/* Question Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3 flex-1">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold shadow-lg">
            {question.student_name?.[0]?.toUpperCase() || 'س'}
          </div>
          <div className="flex-1">
            <p className="font-bold text-gray-900">{question.student_name || 'طالب'}</p>
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <Clock size={12} />
              <time dateTime={question.created_at}>
                {getArabicDate(question.created_at)}
              </time>
              {question.tags && question.tags.length > 0 && (
                <>
                  <span>•</span>
                  <div className="flex gap-1">
                    {question.tags.map((tag, idx) => (
                      <span key={idx} className="bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full text-xs flex items-center gap-1">
                        <Tag size={10} />
                        {tag}
                      </span>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Favorite Button */}
        <button
          onClick={handleFavorite}
          className={`p-2 rounded-lg transition-all ${
            question.is_favorited
              ? 'bg-red-100 text-red-600'
              : 'hover:bg-gray-100 text-gray-400'
          }`}
          aria-label={question.is_favorited ? 'إلغاء التفضيل' : 'إضافة للمفضلة'}
        >
          <Heart size={18} fill={question.is_favorited ? 'currentColor' : 'none'} />
        </button>
      </div>

      {/* Question Text */}
      <p className="text-gray-700 leading-relaxed mb-4 text-sm md:text-base">
        {question.question_text}
      </p>

      {/* Voting */}
      <div className="flex items-center gap-2 mb-4">
        <button
          onClick={() => handleVote('up')}
          className={`p-2 rounded-lg transition-colors ${
            question.user_vote === 'up'
              ? 'bg-green-100 text-green-600'
              : 'hover:bg-gray-100 text-gray-400'
          }`}
          aria-label="تصويت إيجابي"
        >
          <ThumbsUp size={14} fill={question.user_vote === 'up' ? 'currentColor' : 'none'} />
        </button>
        <span className="text-sm text-gray-600 font-medium">{question.upvotes || 0}</span>

        <button
          onClick={() => handleVote('down')}
          className={`p-2 rounded-lg transition-colors ${
            question.user_vote === 'down'
              ? 'bg-red-100 text-red-600'
              : 'hover:bg-gray-100 text-gray-400'
          }`}
          aria-label="تصويت سلبي"
        >
          <ThumbsDown size={14} fill={question.user_vote === 'down' ? 'currentColor' : 'none'} />
        </button>
        <span className="text-sm text-gray-600 font-medium">{question.downvotes || 0}</span>
      </div>

      {/* Teacher Answer */}
      {question.answer_text ? (
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-4 border border-green-100 relative">
          <div className="flex items-center gap-2 mb-2 text-green-700 font-bold text-sm">
            <div className="w-6 h-6 rounded-full bg-green-200 flex items-center justify-center">
              <User size={12} />
            </div>
            {question.teacher_name} (المدرس)
          </div>
          <p className="text-green-900 text-sm leading-relaxed">{question.answer_text}</p>
        </div>
      ) : (
        <div className="text-xs text-amber-600 bg-amber-50 inline-block px-3 py-1 rounded-full border border-amber-100">
          <span className="animate-pulse" aria-hidden="true">⏳</span> بانتظار رد المدرس...
        </div>
      )}
    </div>
  );
};

QuestionSection.propTypes = {
  questions: PropTypes.arrayOf(PropTypes.object),
  isLoading: PropTypes.bool,
  onSubmitQuestion: PropTypes.func.isRequired,
  onVoteQuestion: PropTypes.func.isRequired,
  onFavoriteQuestion: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool,
};

QuestionCard.propTypes = {
  question: PropTypes.shape({
    id: PropTypes.number.isRequired,
    student_name: PropTypes.string,
    question_text: PropTypes.string.isRequired,
    answer_text: PropTypes.string,
    teacher_name: PropTypes.string,
    created_at: PropTypes.string,
    tags: PropTypes.arrayOf(PropTypes.string),
    is_favorited: PropTypes.bool,
    user_vote: PropTypes.string,
    upvotes: PropTypes.number,
    downvotes: PropTypes.number,
  }).isRequired,
  onVote: PropTypes.func.isRequired,
  onFavorite: PropTypes.func.isRequired,
};

export default QuestionSection;
